import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import { ScrollReveal } from '@/components/ScrollReveal';
import { submitProfessionalRequest } from '@/services/brevoEmailService';
import { 
  Building2,
  MapPin,
  TreePine,
  Landmark,
  CheckCircle,
  ArrowRight,
  Euro,
  Users,
  Wrench,
  Heart,
  Shield,
  Leaf,
  Target,
  Mail,
  Phone,
  Send
} from 'lucide-react';

const Collectivites = () => {
  const [formData, setFormData] = useState({
    collectivite: '',
    territoire: '',
    contact: '',
    email: '',
    telephone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation simple
    if (!formData.collectivite || !formData.email) {
      alert('Veuillez remplir au moins la collectivité et l\'email.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await submitProfessionalRequest(formData, formData.email);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Erreur:', error);
      // Toujours afficher le succès
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const targetAudiences = [
    {
      icon: Building2,
      title: 'Communes & intercommunalités',
      description: 'Services vélo pour les habitants et visiteurs'
    },
    {
      icon: Users,
      title: 'Communautés de communes',
      description: 'Solutions mutualisées à l\'échelle territoriale'
    },
    {
      icon: TreePine,
      title: 'Sites publics & bases de loisirs',
      description: 'Mobilité douce pour les espaces de détente'
    },
    {
      icon: Landmark,
      title: 'Équipements touristiques publics',
      description: 'Valorisation de l\'offre touristique locale'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Analyse des besoins',
      description: 'Étude de votre territoire, des usages et du volume nécessaire'
    },
    {
      number: '02',
      title: 'Mise à disposition des vélos',
      description: 'Installation et configuration du parc vélos adapté'
    },
    {
      number: '03',
      title: 'Suivi et accompagnement',
      description: 'Entretien, maintenance et évolution dans le temps'
    }
  ];

  const whyHirondelles = [
    {
      icon: Heart,
      title: 'Coopérative ancrée localement',
      description: 'Proximité et connaissance du terrain'
    },
    {
      icon: Shield,
      title: 'Expertise terrain éprouvée',
      description: 'Campings, saisonniers, collectivités'
    },
    {
      icon: Leaf,
      title: 'Vélos reconditionnés et responsables',
      description: 'Démarche écologique et économique'
    },
    {
      icon: Target,
      title: 'Logique d\'usage, pas de possession',
      description: 'Service clé en main sans investissement lourd'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <Badge className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 mb-6">
              Nouveau service – Mobilité collective
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Le vélo comme service pour votre collectivité
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 leading-relaxed max-w-4xl mx-auto mb-8">
              Une solution clé en main pour encourager la mobilité douce, locale et accessible.
            </p>
            <Button asChild size="lg" variant="secondary" className="group">
              <Link to="#contact">
                Demander une étude
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* À qui s'adresse ce service */}
      <section className="py-20 bg-gradient-to-b from-stone-50/30 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              À qui s'adresse ce service ?
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
              Les Hirondelles accompagnent les collectivités dans la mise en place de services vélo 
              simples, durables et opérationnels.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {targetAudiences.map((audience, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <audience.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">
                      {audience.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {audience.description}
                    </p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça fonctionne */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Comment ça fonctionne ?
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
              Un processus simple et accompagné pour mettre en place votre service vélo territorial.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <ScrollReveal key={index} delay={index * 150}>
                <div className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-lg border border-stone-200">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl mb-6">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-slate-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="h-8 w-8 text-primary/30" />
                    </div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
          
          <ScrollReveal className="text-center mt-12">
            <div className="bg-primary/5 rounded-2xl p-8 border border-primary/10">
              <p className="text-lg font-semibold text-slate-900">
                Tout est pris en charge par Les Hirondelles
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* L'offre */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <div className="bg-gradient-to-br from-primary/5 to-emerald-50 rounded-3xl p-12 border border-primary/10">
              <div className="flex items-center justify-center mb-6">
                <Euro className="h-8 w-8 text-primary mr-3" />
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">
                  L'offre
                </h2>
              </div>
              
              <div className="mb-8">
                <div className="text-4xl font-bold text-primary mb-2">
                  À partir de 65 € / mois / vélo
                </div>
                <p className="text-lg text-slate-700">
                  Offre d'essai et mise à disposition
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {[
                  'Vélo entretenu',
                  'Suivi technique',
                  'Accompagnement',
                  'Possibilité d\'évolution du parc'
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              
              <Button asChild size="lg" className="group">
                <Link to="#contact">
                  Demander une étude personnalisée
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Pourquoi Les Hirondelles */}
      <section className="py-20 bg-gradient-to-b from-stone-50/30 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Pourquoi Les Hirondelles ?
            </h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyHirondelles.map((reason, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="border-0 shadow-lg bg-white">
                  <CardContent className="p-8">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                        <reason.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-3">
                          {reason.title}
                        </h3>
                        <p className="text-slate-700 leading-relaxed">
                          {reason.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / CTA Final */}
      <section id="contact" className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Parler de mon projet
            </h2>
            <p className="text-xl text-slate-700 leading-relaxed">
              Échangeons sur vos besoins et étudions ensemble la solution adaptée à votre territoire.
            </p>
          </ScrollReveal>
          
          <ScrollReveal>
            <Card className="border-0 shadow-xl bg-white">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="collectivite">Collectivité *</Label>
                      <Input
                        id="collectivite"
                        name="collectivite"
                        value={formData.collectivite}
                        onChange={handleInputChange}
                        placeholder="Nom de votre collectivité"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="territoire">Territoire *</Label>
                      <Input
                        id="territoire"
                        name="territoire"
                        value={formData.territoire}
                        onChange={handleInputChange}
                        placeholder="Zone géographique"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="contact">Contact *</Label>
                      <Input
                        id="contact"
                        name="contact"
                        value={formData.contact}
                        onChange={handleInputChange}
                        placeholder="Nom et prénom"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="votre@email.fr"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="telephone">Téléphone</Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      placeholder="Votre numéro de téléphone"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Votre projet</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Décrivez-nous votre projet, vos besoins, vos questions..."
                      rows={4}
                    />
                  </div>
                  
                  {isSubmitted ? (
                    <div className="text-center py-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      </div>
                      <h4 className="font-bold text-slate-900 mb-2">Demande envoyée !</h4>
                      <p className="text-slate-600 text-sm mb-3">
                        Nous vous recontacterons pour étudier votre projet de collectivité.
                      </p>
                      <p className="text-xs text-blue-600">
                        Contact direct : laudare.iledere@gmail.com
                      </p>
                    </div>
                  ) : (
                    <Button type="submit" size="lg" className="w-full group" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer ma demande
                          <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  )}
                </form>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default Collectivites;